/**
 * A fund's card on the dashboard.
 *
 * Shows the current value, the native currency breakdown, where inside the
 * fund the money sits, and a twelve-month sparkline. The location breakdown is
 * the point of the whole model — "касата на входа има 1 430 €" is much less
 * useful than knowing 1 200 of it is in the bank and 80 is with the cashier.
 */
import { Link } from 'react-router-dom';
import { Banknote, ChevronRight, CreditCard, Landmark, Wallet } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { MultiCurrency } from '@/components/Money';
import { Sparkline } from '@/components/Sparkline';
import { colorClasses } from '@/lib/colors';
import { formatMoney } from '@/lib/format';
import type { BalancePoint, FundBalance, Fund, LocationKind } from '@/lib/types';

const LOCATION_ICONS: Record<LocationKind, React.ComponentType<{ className?: string }>> = {
  BANK: Landmark,
  CASH: Banknote,
  CARD: CreditCard,
  OTHER: Wallet,
};

export function FundCard({
  balance,
  fund,
  sparkline,
  baseCurrency,
  baseDecimals,
}: {
  balance: FundBalance;
  fund?: Fund;
  sparkline?: BalancePoint[];
  baseCurrency: string;
  baseDecimals: number;
}): JSX.Element {
  const colors = colorClasses(fund?.color);
  // Taken from the server, not derived here: baseMinor − baseCostMinor also
  // contains the spread paid on exchanges, which is a cost, not FX movement.
  const unrealised = balance.unrealisedMinor;
  const hasForeign = balance.byCurrency.some((c) => c.currency !== baseCurrency);

  return (
    <Card className="group overflow-hidden transition-colors hover:border-foreground/20">
      <Link to={`/funds/${balance.fundId}`} className="block">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              <span className={cn('h-2.5 w-2.5 shrink-0 rounded-full', colors.bg)} />
              <span className="truncate font-medium">{balance.fundName}</span>
            </div>
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
          </div>

          <div className="mt-3 text-2xl font-semibold">
            <MultiCurrency
              totals={balance.byCurrency}
              baseMinor={balance.baseMinor}
              baseCurrency={baseCurrency}
              baseDecimals={baseDecimals}
              compact
            />
          </div>

          {/* Only shown when there is foreign currency — for a fund held purely
              in the base currency this is always exactly zero. */}
          {hasForeign && unrealised !== 0 ? (
            <p className="mt-1 text-xs text-muted-foreground">
              Валутна разлика{' '}
              <span className={cn('tabular', unrealised > 0 ? 'text-income' : 'text-expense')}>
                {unrealised > 0 ? '+' : '−'}
                {formatMoney(Math.abs(unrealised), baseCurrency, baseDecimals)}
              </span>
            </p>
          ) : null}

          {sparkline && sparkline.length > 1 ? (
            <div className="mt-3">
              <Sparkline points={sparkline} />
            </div>
          ) : null}

          <div className="mt-3 space-y-1.5 border-t pt-3">
            {balance.byLocation.map((loc) => {
              const Icon = LOCATION_ICONS[loc.locationKind] ?? Wallet;
              return (
                <div key={loc.locationId} className="flex items-center justify-between gap-2 text-sm">
                  <span className="flex min-w-0 items-center gap-1.5 text-muted-foreground">
                    <Icon className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">
                      {loc.locationName}
                      {loc.holderName ? ` · ${loc.holderName}` : ''}
                    </span>
                  </span>
                  <MultiCurrency
                    totals={loc.byCurrency}
                    baseMinor={loc.baseMinor}
                    baseCurrency={baseCurrency}
                    baseDecimals={baseDecimals}
                    className="shrink-0 text-sm"
                    compact
                  />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Link>
    </Card>
  );
}
